/**
 * Bundle_Service.ts
 * Bundle component management + price propagation
 * (sk-catalog-api /api/v1/items/:id/components | /component-price-impact | /price | /relink-price)
 */

import axiosInstance from '../../api/axiosInstance';
import {
  Catalog_Item_Components_Url,
  Catalog_Item_Component_Url,
  Catalog_Item_PriceImpact_Url,
  Catalog_Item_Price_Url,
  Catalog_Item_RelinkPrice_Url,
  Catalog_AttentionQueue_Url,
} from '../../api/endpoints';
import {
  AddComponentPayload,
  UpdateComponentPayload,
  ComponentsResponse,
  PriceImpactParams,
  PriceImpactResponse,
  CommitPricePayload,
  RelinkPricePayload,
  BundlePriceResponse,
} from '../../schemas/catalog/bundle';

/* ─────────────────────────────────────────
   List components — GET /api/v1/items/:id/components
───────────────────────────────────────── */

export const getComponents = async (bundleId: string): Promise<ComponentsResponse> => {
  const { data } = await axiosInstance.get<ComponentsResponse>(Catalog_Item_Components_Url(bundleId));
  return data;
};

/* ─────────────────────────────────────────
   Add component — POST /api/v1/items/:id/components
───────────────────────────────────────── */

export const addComponent = async (bundleId: string, payload: AddComponentPayload): Promise<ComponentsResponse> => {
  const { data } = await axiosInstance.post<ComponentsResponse>(Catalog_Item_Components_Url(bundleId), payload);
  return data;
};

/* ─────────────────────────────────────────
   Update component — PATCH /api/v1/items/:id/components/:childId
───────────────────────────────────────── */

export const updateComponent = async (
  bundleId: string,
  childId: string,
  payload: UpdateComponentPayload,
): Promise<ComponentsResponse> => {
  const { data } = await axiosInstance.patch<ComponentsResponse>(
    Catalog_Item_Component_Url(bundleId, childId),
    payload,
  );
  return data;
};

/* ─────────────────────────────────────────
   Remove component — DELETE /api/v1/items/:id/components/:childId
───────────────────────────────────────── */

export const removeComponent = async (bundleId: string, childId: string): Promise<void> => {
  await axiosInstance.delete(Catalog_Item_Component_Url(bundleId, childId));
};

/* ─────────────────────────────────────────
   Price impact preview — GET /api/v1/items/:id/component-price-impact
   Read-only: shows how a component change would ripple to ancestors.
───────────────────────────────────────── */

export const getPriceImpact = async (itemId: string, params: PriceImpactParams): Promise<PriceImpactResponse> => {
  const { data } = await axiosInstance.get<PriceImpactResponse>(Catalog_Item_PriceImpact_Url(itemId), { params });
  return data;
};

/* ─────────────────────────────────────────
   Commit price — POST /api/v1/items/:id/price
   Applies a price change + propagates to ancestor bundles.
───────────────────────────────────────── */

export const commitPrice = async (itemId: string, payload: CommitPricePayload): Promise<BundlePriceResponse> => {
  const { data } = await axiosInstance.post<BundlePriceResponse>(Catalog_Item_Price_Url(itemId), payload);
  return data;
};

/* ─────────────────────────────────────────
   Relink price — POST /api/v1/items/:id/relink-price
   Clears PRICE_OVERRIDDEN and recomputes the rollup from components.
───────────────────────────────────────── */

export const relinkPrice = async (
  itemId: string,
  payload: RelinkPricePayload = { mode: 'recompute' },
): Promise<BundlePriceResponse> => {
  const { data } = await axiosInstance.post<BundlePriceResponse>(Catalog_Item_RelinkPrice_Url(itemId), payload);
  return data;
};

/* ─────────────────────────────────────────
   Attention queue — GET /api/v1/attention-queue
   Items flagged for price review (stale rollups, overrides, etc.).
───────────────────────────────────────── */

export const getAttentionQueue = async (params?: Record<string, any>): Promise<any> => {
  const { data } = await axiosInstance.get(Catalog_AttentionQueue_Url, { params });
  return data;
};
